import React, { useState, useEffect } from "react";
import GenericModal from "./GenericModal";
import OTPVerificationModal from "./OTPVerificationModal";
import useAuthStore from "../../store/useAuthStore";
import toast from "react-hot-toast";
import { axiosInstance } from "../../lib/axiosInstance";
import {
  UserIcon,
  AtSymbolIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 48 48">
    <path
      fill="#EA4335"
      d="M24 9.5c3.9 0 6.7 1.6 8.3 3.1l6.3-6.3C34.9 2.7 30 .7 24 .7 14.8.7 7.2 6.3 4.4 14.3l7.3 5.7C13.3 13.1 18.2 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.3 24.5c0-1.6-.1-3.1-.4-4.6H24v8.8h12.5c-.5 2.8-2.2 5.2-4.9 6.9l7.3 5.7c4.3-3.9 6.8-9.7 6.8-16.8z"
    />
    <path
      fill="#FBBC05"
      d="M11.7 28.1c-.4-1.2-.6-2.5-.6-3.9s.2-2.7.6-3.9l-7.3-5.7C2.7 18.1 1.7 21 1.7 24.2s1 6.1 4.7 9.8l7.3-5.9z"
    />
    <path
      fill="#34A853"
      d="M24 47.3c6.5 0 11.9-2.1 15.8-5.7l-7.3-5.7c-2.1 1.4-4.9 2.3-8.5 2.3-5.9 0-10.9-3.9-12.7-9.2l-7.3 5.7C7.2 41 14.8 47.3 24 47.3z"
    />
    <path fill="none" d="M0 0h48v48H0z" />
  </svg>
);

const SignupModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { customerSignup } = useAuthStore();

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setLoading(false);
        setOtpLoading(false);
        setGoogleLoading(false);
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Reset email verification if email changes
    if (name === "email" && emailVerified) {
      setEmailVerified(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setOtpLoading(true);
    try {
      const response = await axiosInstance.post("/customer/send-signup-otp", {
        email: formData.email,
      });
      if (response.data.success) {
        setShowOtpModal(true);
        toast.success("OTP sent successfully to your email!");
      } else {
        toast.error(response.data.message || "Failed to send OTP.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to send OTP. Please try again."
      );
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerificationSuccess = () => {
    setEmailVerified(true);
    toast.success("Email verified! You can now complete your signup.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!emailVerified) {
      toast.error("Please verify your email first.");
      return;
    }

    if (
      !formData.name.trim() ||
      !formData.email ||
      !formData.password
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    setLoading(true);
    try {
      // Call signup without OTP since it's already verified
      const response = await axiosInstance.post("/customer/signup", {
        name: formData.name,
        email: formData.email,
        password: formData.password,
      });

      if (response.data.success) {
        toast.success("Account created successfully!");
        onSuccess();
        onClose();
      } else {
        toast.error(response.data.message || "Signup failed.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Signup failed. Please try again."
      );
      console.error("Signup from modal failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignupClick = async () => {
    setGoogleLoading(true);
    try {
      // --- MODIFICATION START ---
      sessionStorage.setItem('googleAuthStatus', 'in-progress');
      sessionStorage.setItem(
        "googleAuthRedirect",
        window.location.pathname + window.location.search
      );
      // --- MODIFICATION END ---
      const response = await axiosInstance.get("/customer/google/url");
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error("Could not get Google signup URL.");
        setGoogleLoading(false);
        // --- MODIFICATION START ---
        sessionStorage.removeItem('googleAuthStatus');
        sessionStorage.removeItem('googleAuthRedirect');
        // --- MODIFICATION END ---
      }
    } catch (error) {
      toast.error("Error initiating Google signup.");
      console.error("Google URL fetch error:", error);
      setGoogleLoading(false);
      // --- MODIFICATION START ---
      sessionStorage.removeItem('googleAuthStatus');
      sessionStorage.removeItem('googleAuthRedirect');
      // --- MODIFICATION END ---
    }
  };

  const handleClose = () => {
    setFormData({ name: "", email: "", password: "" });
    setEmailVerified(false);
    setShowOtpModal(false);
    setLoading(false);
    setOtpLoading(false);
    setGoogleLoading(false);
    setShowPassword(false);
    onClose();
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Customer Account"
      size="max-w-md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full Name
          </label>
          <div className="relative">
            <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full pl-10 pr-3 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="John Doe"
              required
              disabled={loading || googleLoading}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email {emailVerified && <span className="text-green-600 text-xs">(✓ Verified)</span>}
          </label>
          <div className="relative">
            <AtSymbolIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full pl-10 pr-3 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="you@example.com"
              required
              disabled={loading || otpLoading || emailVerified || googleLoading}
            />
          </div>
        </div>
        {!emailVerified && (
          <button
            type="button"
            onClick={handleVerifyEmail}
            disabled={otpLoading || !formData.email || googleLoading}
            className="w-full bg-blue-600 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center justify-center disabled:opacity-60"
          >
            {otpLoading ? (
              <AiOutlineLoading3Quarters className="animate-spin text-xl" />
            ) : (
              "Verify Email"
            )}
          </button>
        )}

        {emailVerified && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <LockClosedIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-10 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Create a password"
                  required
                  disabled={loading || googleLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading || googleLoading}
              className="w-full bg-green-600 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-green-700 transition duration-200 flex items-center justify-center disabled:opacity-60"
            >
              {loading ? (
                <AiOutlineLoading3Quarters className="animate-spin text-xl" />
              ) : (
                "Create Account"
              )}
            </button>
          </>
        )}

        <div className="flex items-center my-3">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="flex-shrink mx-4 text-gray-400 text-sm">OR</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>
        <button
          type="button"
          onClick={handleGoogleSignupClick}
          disabled={loading || otpLoading || googleLoading}
          className="w-full border flex items-center justify-center gap-2 border-slate-200 rounded-lg text-slate-700 hover:border-slate-400 hover:text-slate-900 hover:shadow transition duration-150 py-2.5 disabled:opacity-60"
        >
          {googleLoading ? (
            <AiOutlineLoading3Quarters className="animate-spin text-xl" />
          ) : (
            <>
              <GoogleIcon />
              <span>Sign up with Google</span>
            </>
          )}
        </button>
      </form>

      <OTPVerificationModal
        isOpen={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        email={formData.email}
        onVerificationSuccess={handleVerificationSuccess}
      />
    </GenericModal>
  );
};

export default SignupModal;